import { useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import OTPInput from '../Form/OTPInput';
import Modal from '../Modal/Modal';
import { Button, ButtonSize, ButtonStyles, ButtonWidth } from '../Form';
import { Theme } from '../types/enums';
import './index.scss';

interface OTPModalProps {
  email: string;
  countdown: number;
  onClose: () => void;
  onRequestOTP: (mode?: string) => Promise<boolean>;
  onVerifyOTP: (otp: string) => Promise<boolean>;

  startCountdown: () => void;
}

const OTPModal = ({ email, countdown, onClose, onRequestOTP, onVerifyOTP, startCountdown }: OTPModalProps) => {
  const { t } = useTranslation();
  
  const [otpCode, setOtpCode] = useState<string | null>(null);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | JSX.Element | null>(null);

  const handleInputOTP = (otp: string) => {
    setOtpErrorMessage(null);
    setOtpCode(otp);
  };

  const handleVerifyOTP = async () => {
    if (!otpCode) return;

    const isSuccess = await onVerifyOTP(otpCode);

    if (!isSuccess) {
      setOtpErrorMessage(
        <>
          <FaExclamationCircle />
          {t('dashboard.one-time-password.modal.message.error.invalidPasscode')}
        </>,
      );
    }
  };

  const handleRequestOTP = async () => {
    const isRequestSucess = await onRequestOTP();
    if (isRequestSucess) {
      startCountdown();
    }
  };

  return (
    <Modal
      className="otp-modal"
      headline={<h2>{t('dashboard.one-time-password.modal.label.title')}</h2>}
      onClose={onClose}
      closeIcon={true}
    >
      <div className="drawer-verify-email">
        <p>{t('dashboard.one-time-password.modal.label.passcodeSentTo')}</p>
        <p>
          <b>{email}</b>
        </p>
        <div className="otp-modal__input-container">
          <OTPInput length={4} onChange={handleInputOTP} errorMessage={otpErrorMessage} />
        </div>
        <div>
          <span>{t('dashboard.one-time-password.modal.label.didntReceiveCode')}</span>
          <button
            className={`resend-btn ${countdown > 0 ? 'resend-btn--disabled' : 'resend-btn--enabled'}`}
            onClick={handleRequestOTP}
            disabled={countdown > 0}
          >
            {countdown > 0 ? `${String(countdown).padStart(2, '0')}s` : t('dashboard.one-time-password.modal.label.resend')}
          </button>
        </div>
        <div className={'otp-modal__cta-btn'}>
          <Button
            title={t('dashboard.one-time-password.modal.button.next')}
            onClick={handleVerifyOTP}
            size={ButtonSize.SMALL}
            disabled={!otpCode || otpCode.length < 4 || !!otpErrorMessage}
            buttonWidth={ButtonWidth.FULL_WIDTH}
            buttonStyle={ButtonStyles.SOLID}
            theme={Theme.GREEN}
            style={{ padding: '9px 1.5rem' }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default OTPModal;
